import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DeployComponent } from "../../deploy/Deploy";
import ErrorDisplay from "../../../components/error/ErrorDisplay";
import { SessionView } from "../../session/Session";
import Editor, { OnErrorFunc, OnFinishedFunc, Problem } from "../Editor";
import { RoleIsReadOnly } from "../../../components/role";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, IconButton } from "@mui/material";
import { getHeaders } from "../../../utils/common";
import StatusBar from "../../../components/StatusBar";
import Loading from "../../../Loading";
/**
 * @type {LoadResponse}
 */
type LoadResponse = {
  value: string;
  lints: Problem[];
  errors: Problem[];
  view?: SessionView;
};

/**
 * @type {appParams}
 */
type appParams = {
  sessionID: string;
  fileName: string;
};

/**
 * config file editor - yaml editor
 * @returns {JSX.Element}
 */
export default function App() {
  const navigate = useNavigate();

  const { sessionID, fileName } = useParams<appParams>();
  const graphURL = `/app/view/s/${sessionID}/${fileName}/graph`;

  // Copy file modal
  const [copyFileModalIsOpen, setCopyFileModalIsOpen] = useState(false);
  const [deploySelected, setDeploySelected] = useState<string>("");

  const [isLoading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [config, setConfig] = useState("");
  const [warnings, setWarnings] = useState<Problem[]>([]);
  const [errors, setErrors] = useState<Problem[]>([]);
  const [readOnly, setReadOnly] = useState(true);
  const [saveState, setSaveState] = useState("");
  const [sessView, setSessView] = useState<SessionView | null>(null);

  // Set the default view to match this one.
  localStorage.setItem("defaultFileView", "config");

  const configURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/config`;

  const myHeaders = getHeaders();
  myHeaders.append("Content-Type", "application/json");

  useEffect(() => {
    let subscribed = true;

    setSaveState("");
    setLoading(true);

    fetch(configURL, { headers: myHeaders, method: "GET" })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.text();
        throw new Error(`${response.statusText}: ${text}`);
      })
      .then((json: LoadResponse) => {
        if (!subscribed) {
          return;
        }

        setConfig(json.value);
        setWarnings(json.lints);
        setErrors(json.errors);
        setReadOnly(RoleIsReadOnly(json.view?.user_role || ""));
        setFetchError(null);

        setSessView(json.view || null);
      })
      .catch((error) => (subscribed ? setFetchError(error) : null))
      .finally(() => (subscribed ? setLoading(false) : null));

    return () => {
      subscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configURL, sessionID, fileName]);

  const saveConfig = useCallback(
    (
      saveConfig: string | null,
      onFinished: OnFinishedFunc,
      onError: OnErrorFunc
    ) => {
      let subscribed = true;

      setSaveState("saving");

      fetch(configURL, {
        method: "POST",
        headers: myHeaders,
        body: saveConfig,
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json();
          }
          const text = await response.text();
          throw new Error(`${response.statusText}: ${text}`);
        })
        .then((json) => {
          if (!subscribed) {
            return;
          }
          setSaveState("saved");
          onFinished(json.lints, json.errors);
        })
        .catch((error) => {
          if (!subscribed) {
            return;
          }
          setSaveState("failed");
          onError(error);
        });

      return () => (subscribed = false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionID, fileName, configURL]
  );
  const downloadFile = () => {
    const url = `/mqttapi/v1/session/${sessionID}/download/${fileName}`;
    fetch(url, { headers: myHeaders, method: "GET" })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.text();
        throw new Error(`${response.statusText}: ${text}`);
      })
      .then((res) => {
        const blob = new Blob([res], {
          type: "application/json;charset=utf-8",
        });
        saveAs(blob, `${fileName}`);
      })
      .catch((error) => console.log(error));
  };

  return (
    <Box style={{ height: "88vh", width: "100%" }}>
      {/* <CopyFileModal
        sessionID={sessionID}
        target={decodeURIComponent(fileName)}
        onCopy={(sessionID: string, fileName: string) => {
          history.push(`/app/view/s/${sessionID}/${encodeURIComponent(fileName)}/config`)
        }}
        isOpen={copyFileModalIsOpen}
        closeModal={() => setCopyFileModalIsOpen(false)}
        organisation={
          sessView?.organisation_name
            ? {
                id: sessView.owner_id,
                name: sessView.organisation_name,
              }
            : undefined
        }
      /> */}

      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <IconButton
          title="switch to graph view "
          onClick={() => navigate(graphURL)}
        >
          <AccountTreeIcon />
        </IconButton>
        <IconButton
          title="copy file to..."
          onClick={() => {
            setCopyFileModalIsOpen(true);
          }}
        >
          <ContentCopyIcon />
        </IconButton>
        <IconButton title="download file" onClick={downloadFile}>
          <DownloadIcon />
        </IconButton>
        <DeployComponent
          deploySelected={deploySelected}
          setDeploySelected={setDeploySelected}
        />
        <small>{saveState}</small>
      </Box>
      <Box>
        {isLoading ? (
          <Loading />
        ) : fetchError !== null ? (
          <ErrorDisplay err={fetchError} url={configURL} />
        ) : (
          <Editor
            name="bstdio_editor_config"
            contents={config}
            initWarnings={warnings}
            initErrors={errors}
            mode="yaml"
            readOnly={readOnly}
            onSave={saveConfig}
            onChange={() => setSaveState("pending")}
          ></Editor>
        )}
      </Box>
    </Box>
  );
}
